/**
 * The document root (SCENE_ROOT_SPEC D8): the one authored root node of the
 * open document, and the default parent for anything that lands "at the top".
 *
 * The engine's own root (`TraceScene.getSceneRoot`) is a runtime mount point:
 * never authored, never serialized, never a target. Neither root may be
 * deleted or reparented. The document root may be renamed, transformed and
 * given behaviors; the engine root is never offered at all.
 *
 * Which node is the document root is what the session was established with
 * (`TraceScene.getSessionDocumentRoot`), not the shape of the tree: in a
 * session that never adopted one, the engine root's single child is just the
 * first object created. Adopting a root is the host's call when a session
 * starts; these predicates only read the answer.
 *
 * The editor root (CONTENT_MODEL_SPEC §2.9) hangs under the document root but
 * is in a different frame, which {@link mountEditorRoot} poses it in.
 */

import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';

/** The open document's root, or null when the session has none. */
export function documentRoot(scene: TraceScene | null | undefined): SceneObject | null {
    return scene?.getSessionDocumentRoot() ?? null;
}

/**
 * Where an object that belongs at the top of the scene is parented: the
 * document root, else the engine root for a session that has none, so every
 * creation path still works.
 */
export function topLevelParent(scene: TraceScene | null | undefined): SceneObject | null {
    return documentRoot(scene) ?? scene?.getSceneRoot() ?? null;
}

/** The scene's own top-level objects: the children of {@link topLevelParent}. */
export function topLevelObjects(scene: TraceScene | null | undefined): SceneObject[] {
    return topLevelParent(scene)?.getChildren() ?? [];
}

/** Whether `object` is the engine's mount point. */
export function isEngineRoot(object: SceneObject | null | undefined): boolean {
    return !!object && object.traceScene.getSceneRoot() === object;
}

/** Whether `object` is the document root of its own session. */
export function isDocumentRoot(object: SceneObject | null | undefined): boolean {
    return !!object && documentRoot(object.traceScene) === object;
}

/** Whether `object` is the engine root or the document root. */
export function isStructuralRoot(object: SceneObject | null | undefined): boolean {
    return isEngineRoot(object) || isDocumentRoot(object);
}

/**
 * Why `object` may not be deleted, reparented or duplicated, or null when it
 * may. One message serves every verb, since the document root is the only
 * root a user can aim at. A copy of the document root would be a second root.
 */
export function structuralRootRefusal(
    object: SceneObject | null | undefined,
    verb: 'delete' | 'move' | 'duplicate',
): string | null {
    if (!object) return null;
    const past = verb === 'delete' ? 'deleted' : verb === 'move' ? 'reparented' : 'duplicated';
    if (isDocumentRoot(object)) {
        return `"${object.name}" is the root of this document and cannot be ${past}`
            + ' — rename or transform it instead (SCENE_ROOT_SPEC D8)';
    }
    if (isEngineRoot(object)) {
        return `The engine scene root cannot be ${past} — it is a runtime mount point,`
            + ' not a node (SCENE_ROOT_SPEC D8)';
    }
    return null;
}

/** The editor roots mounted in this runtime. Never written: a session fact. */
const editorRoots = new WeakSet<SceneObject>();

/**
 * Mount `root` as the session's editor root (CONTENT_MODEL_SPEC §2.9): under
 * `parent`, the document root by default, posed in the scene frame.
 *
 * The scene frame is the editor's world, the frame editor content is authored
 * in and placement poses are expressed in. The node the root hangs under is
 * posed for the image anchor's preview (pitched, lifted above y = 0, or at a
 * placement), so the root's local pose is the inverse of that node's world
 * pose, whatever the document saved. From then on its transform is not
 * editable ({@link isEditorRoot}).
 *
 * The pose holds while its ancestors keep theirs. The anchor-host poses that
 * move content with the host re-mount it ({@link remountEditorRoots}); one
 * that keeps the host's children in place leaves it there. An undo that
 * rebuilds the root makes a new object, which the host mounts again.
 *
 * Does nothing without a parent, or when `parent` is `root` or under it.
 */
export function mountEditorRoot(root: SceneObject, parent: SceneObject | null = topLevelParent(root.traceScene)): void {
    for (let n = parent; n; n = n.getParent()) if (n === root) return;
    if (!parent) return;
    if (root.getParent() !== parent) root.setParent(parent);
    const position = new Vector3();
    const rotation = new Quaternion();
    const scaling = new Vector3();
    Matrix.Invert(freshWorld(parent.transform)).decompose(scaling, rotation, position);
    root.transform.position.copyFrom(position);
    root.transform.rotationQuaternion = rotation;
    root.transform.scaling.copyFrom(scaling);
    editorRoots.add(root);
}

/** Mount again, where they hang, the editor roots under `host`, after `host` was re-posed. */
export function remountEditorRoots(host: SceneObject): void {
    for (const root of editorRootsUnder(host)) mountEditorRoot(root, root.getParent());
}

/** The editor roots under `host`, not counting any under another editor root. */
export function editorRootsUnder(host: SceneObject): SceneObject[] {
    return host.getChildren().flatMap(child => isEditorRoot(child) ? [child] : editorRootsUnder(child));
}

/** Whether `object` was mounted as an editor root ({@link mountEditorRoot}). */
export function isEditorRoot(object: SceneObject | null | undefined): boolean {
    return !!object && editorRoots.has(object);
}

/** World matrix with every ancestor recomputed first: a cached one may be stale. */
function freshWorld(node: TransformNode): Matrix {
    const chain: TransformNode[] = [];
    for (let n: TransformNode | null = node; n; n = n.parent as TransformNode | null) chain.unshift(n);
    for (const n of chain) n.computeWorldMatrix(true);
    return node.getWorldMatrix().clone();
}
